import React, { ReactNode, useCallback, useRef } from 'react';
import { View } from 'react-native';
import { Easing, useSharedValue, withTiming } from 'react-native-reanimated';
import { DashboardScrollView } from '@/components/dashboard-scroll-view';
import { PageHeader } from '@/components/ui/page-header';

interface PageLayoutProps {
  /** Título da página */
  title: string;
  /** Subtítulo/descrição opcional */
  subtitle?: string;
  /** Sub-página: mostra chevron de voltar ao lado do título */
  onBack?: () => void;
  /** Desabilita o botão voltar (ex.: durante submit) */
  backDisabled?: boolean;
  /** Título do botão de ação à direita do cabeçalho */
  buttonTitle?: string;
  /** Handler do botão de ação */
  onButtonPress?: () => void;
  /** Variante do botão de ação */
  buttonVariant?: 'default' | 'outline' | 'ghost' | 'link';
  /** testID repassado ao botão */
  buttonTestID?: string;
  /** Componente customizado no lugar do botão padrão */
  headerAction?: ReactNode;
  /** Node fixo no rodapé, fora do scroll (ex.: CTA/salvar sticky) */
  footer?: ReactNode;
  /** Props extras repassadas ao DashboardScrollView (refreshControl, testID, etc.) */
  scrollProps?: Partial<React.ComponentProps<typeof DashboardScrollView>>;
  /** Conteúdo da página abaixo do cabeçalho */
  children: ReactNode;
}

/** Scroll a partir do qual a faixa do cabeçalho entra no estado compacto. */
const COMPACT_THRESHOLD = 24;

/**
 * Layout padrão de tela. Fonte da verdade: tela de Contratos.
 * Cabeçalho é uma faixa FIXA acima do scroll (irmã do `DashboardScrollView`,
 * não filha) — RN não tem `position: sticky`, então o resultado equivalente
 * é dois irmãos empilhados. A faixa encolhe num passo eased quando a lista
 * rola além de `COMPACT_THRESHOLD`; ver `PageHeader` (`asStrip`) pela
 * animação em si.
 * Telas tab-raiz: sem `onBack`. Sub-páginas empurradas: passe `onBack` para
 * exibir o chevron ao lado do título — mesmo espaçamento, sem variantes.
 */
export function PageLayout({
  title,
  subtitle,
  onBack,
  backDisabled,
  buttonTitle,
  onButtonPress,
  buttonVariant = 'default',
  buttonTestID,
  headerAction,
  footer,
  scrollProps,
  children,
}: PageLayoutProps) {
  const compactProgress = useSharedValue(0);
  // Guarda o booleano atual para só disparar o withTiming quando ele VIRA —
  // nunca a cada evento de scroll (regra calm-ui: um passo eased, não
  // interpolação contínua acompanhando o dedo).
  const isCompactRef = useRef(false);

  const handleScrollOffset = useCallback(
    (y: number) => {
      const compact = y > COMPACT_THRESHOLD;
      if (compact === isCompactRef.current) return;
      isCompactRef.current = compact;
      compactProgress.value = withTiming(compact ? 1 : 0, {
        duration: 200,
        easing: Easing.out(Easing.cubic),
      });
    },
    [compactProgress],
  );

  return (
    <View className="flex-1 bg-canvas">
      <PageHeader
        title={title}
        subtitle={subtitle}
        onBack={onBack}
        backDisabled={backDisabled}
        buttonTitle={buttonTitle}
        onButtonPress={onButtonPress}
        buttonVariant={buttonVariant}
        buttonTestID={buttonTestID}
        action={headerAction}
        asStrip
        compactProgress={compactProgress}
      />
      <DashboardScrollView
        className="w-full flex-1 bg-canvas"
        contentContainerStyle={{ paddingBottom: footer ? 110 : 80 }}
        {...scrollProps}
        onScrollOffset={handleScrollOffset}
      >
        <View className="gap-4 p-5">{children}</View>
      </DashboardScrollView>
      {footer}
    </View>
  );
}
